import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { SoundTouchNode } from '@soundtouchjs/audio-worklet'
import processorUrl from '@soundtouchjs/audio-worklet/processor?url'

/**
 * One audio element for the whole app.
 *
 * The inline <Player> in ui.jsx owns its own <audio>, which is right for a
 * song you're studying on /learn — it's the screen's subject. It's wrong for
 * browsing: tapping play on Home and then opening Circles unmounted the
 * element and the song stopped. This provider sits ABOVE the router, so the
 * element is never unmounted by navigation and playback survives the trip.
 *
 * Deliberately dumb about Supabase. Callers already invoke r2-playback-url to
 * get a signed URL; they pass the resolved `src` in. Putting the fetch in here
 * would mean the provider needed the session, the edge function, and error
 * surfacing, none of which is its job.
 *
 * Audio only. A video recording stays with the inline player — a mini bar is
 * the wrong shape for something you have to watch.
 */

const Ctx = createContext(null)

/** Semitone range a listener can dial in during playback. Standard is ±3;
    ±4/±5 are the "extended" range the UI marks as sounding different. */
const MAX_SEMITONES = 5

export function PlayerProvider({ children }) {
  const el = useRef(null)
  const graph = useRef(null)         // { ctx, source, stNode } — built lazily, once per element
  const graphPromise = useRef(null)  // in-flight/settled build, so callers don't race each other
  const [track, setTrack] = useState(null)
  const [playing, setPlaying] = useState(false)
  const [pos, setPos] = useState(0)
  const [len, setLen] = useState(0)
  const [failed, setFailed] = useState(false)
  const [semitones, setSemitonesState] = useState(0)
  const semitonesRef = useRef(0)   // read from callbacks that must not go stale

  /* Pitch-shift is real-time, in-browser, on audio already loaded for
     playback — it adds no new source, no new fetch, no new decrypted or
     cached copy of anything. A MediaElementSourceNode can only be created
     once per <audio> element, so the Web Audio graph is built lazily and kept
     for the life of the app — on first TRANSPOSE, not on first play.
     Routing the element through Web Audio is not free: a bare <audio> keeps
     playing when the app is backgrounded and one piped into an AudioContext
     does not, because iOS suspends the context and the song stops. Building
     it on every play charged that cost to every listener to serve the few who
     shift the key. There is no way back out either — createMediaElementSource
     is one-shot per element — so once someone transposes, that session stays
     routed for as long as the tab lives.
     SoundTouchNode does WSOLA time-stretch pitch shifting on the audio
     rendering thread (an AudioWorklet), not the delay-line/granular
     technique — that's what fixes the warbly artifact. It does NOT preserve
     vocal formants, so a shifted voice still changes timbre ("chipmunk" on
     the way up); @soundtouchjs/formant-correction-worklet claims to fix
     that but its correction measurably did nothing in testing (identical
     output spectrum with formantStrength 0 vs 1) — reverted rather than
     ship a dependency that doesn't do what it says. */
  const ensureGraph = useCallback(() => {
    if (graph.current) return Promise.resolve(graph.current)
    if (graphPromise.current) return graphPromise.current
    graphPromise.current = (async () => {
      const m = el.current
      if (!m) return null
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      await SoundTouchNode.register(ctx, processorUrl)
      const stNode = new SoundTouchNode({ context: ctx })
      stNode.connect(ctx.destination)
      const source = ctx.createMediaElementSource(m)
      source.connect(stNode)
      m.preservesPitch = false // SoundTouch owns pitch; don't double-correct
      const g = { ctx, source, stNode }
      graph.current = g
      return g
    })()
    return graphPromise.current
  }, [])

  /**
   * Wake the graph and re-assert the pitch on a clock that is actually running.
   *
   * pitchSemitones is an AudioParam, and `param.value = x` is defined as
   * setValueAtTime(x, ctx.currentTime). A suspended context's currentTime is
   * FROZEN, so a write made while backgrounded pins itself to a stale moment
   * and the audible pitch can end up somewhere other than what the UI shows —
   * which is the song coming back out of key. Resume first, then write.
   *
   * No graph means nothing was ever transposed, and a bare <audio> element
   * needs none of this.
   */
  const resumeGraph = useCallback(async () => {
    const g = graph.current
    if (!g) return
    if (g.ctx.state === 'suspended') {
      try { await g.ctx.resume() } catch { /* a blocked resume must not break play */ }
    }
    const now = g.ctx.currentTime
    g.stNode.pitchSemitones.cancelScheduledValues(now)
    g.stNode.pitchSemitones.setValueAtTime(semitonesRef.current, now)
  }, [])

  /** Semitones, clamped to the standard+extended range. Tempo is untouched —
      playbackRate on both the node and the element stays at 1.0 throughout;
      only pitchSemitones moves. */
  const setSemitones = useCallback((n) => {
    const clamped = Math.max(-MAX_SEMITONES, Math.min(MAX_SEMITONES, n))
    semitonesRef.current = clamped
    setSemitonesState(clamped)
    // Zero with no graph is already zero — building one here would cost the
    // listener background playback to apply a shift of nothing.
    if (clamped === 0 && !graph.current) return
    ensureGraph().then(() => resumeGraph())
  }, [ensureGraph, resumeGraph])

  /** Start a track, or toggle it if it's already the one loaded. */
  const play = useCallback((next) => {
    const m = el.current
    if (!m) return
    if (track && next.id === track.id) {
      if (m.paused) { resumeGraph(); m.play().catch(() => setFailed(true)) } else { m.pause() }
      return
    }
    setFailed(false); setPos(0); setLen(next.duration || 0)
    setTrack(next)
    // src is set on the element directly, NOT through render. Waiting for
    // React to commit the new src before calling play() means play() can fire
    // against the previous song's src — the element is real and mutable, so
    // there is nothing to gain by routing this through state.
    m.src = next.src
    m.currentTime = 0
    setSemitones(0)  // per-session, resets on every new song
    resumeGraph()    // no-op unless a previous song built the graph
    m.play().catch(() => setFailed(true))
  }, [track, setSemitones, resumeGraph])

  const toggle = useCallback(() => {
    const m = el.current
    if (!m || !track) return
    if (m.paused) {
      // The mini-player's play button lands here, which is the path back after
      // the app has been in the background. Without this the element runs
      // against a suspended context and nothing comes out.
      resumeGraph()
      m.play().catch(() => setFailed(true))
    } else m.pause()
  }, [track, resumeGraph])

  const close = useCallback(() => {
    const m = el.current
    if (m) { m.pause(); m.removeAttribute('src'); m.load() }
    setTrack(null); setPlaying(false); setPos(0); setLen(0); setFailed(false)
    setSemitones(0)
  }, [setSemitones])

  const seek = useCallback((fraction) => {
    const m = el.current
    if (!m || !len) return
    m.currentTime = Math.max(0, Math.min(len, fraction * len))
  }, [len])

  /* Lock-screen and headphone controls. Cheap to add, and their absence is
     one of the louder "this is a website" tells on a phone. */
  useEffect(() => {
    if (!('mediaSession' in navigator) || !track) return
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: track.title || 'Untitled',
      artist: track.writer || 'Beseter',
    })
    navigator.mediaSession.setActionHandler('play', () => { resumeGraph(); el.current?.play() })
    navigator.mediaSession.setActionHandler('pause', () => el.current?.pause())
  }, [track, resumeGraph])

  /* Coming back from the background. The context was suspended while we were
     away, so anything still routed through it is silent — or, worse, resumes
     at whatever pitch was pinned to the frozen clock. Waking it here means the
     song is right before the listener touches anything, rather than after they
     have already heard it wrong. pagehide/pageshow covers the iOS back-forward
     cache, which does not fire visibilitychange on restore. */
  useEffect(() => {
    const wake = () => { if (document.visibilityState === 'visible') resumeGraph() }
    document.addEventListener('visibilitychange', wake)
    window.addEventListener('pageshow', wake)
    return () => {
      document.removeEventListener('visibilitychange', wake)
      window.removeEventListener('pageshow', wake)
    }
  }, [resumeGraph])

  const value = useMemo(() => ({
    track, playing, pos, len, failed, play, toggle, close, seek,
    semitones, setSemitones, maxSemitones: MAX_SEMITONES,
    isCurrent: (id) => track?.id === id,
  }), [track, playing, pos, len, failed, play, toggle, close, seek, semitones, setSemitones])

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* No src prop — see play()/close(). React must not own this attribute
          or it will reset the element mid-playback on unrelated re-renders. */}
      <audio
        ref={el}
        preload="metadata"
        crossOrigin="anonymous"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) => setLen(e.target.duration || 0)}
        onTimeUpdate={(e) => setPos(e.target.currentTime)}
        onEnded={() => { setPlaying(false); setPos(0) }}
        onError={() => { if (el.current?.getAttribute('src')) setFailed(true) }}
      />
    </Ctx.Provider>
  )
}

export function usePlayer() {
  const v = useContext(Ctx)
  if (!v) throw new Error('usePlayer must be used inside <PlayerProvider>')
  return v
}
